import { Button, DataTablePagination } from 'components/common'
import DataTableImportButton from 'components/common/dataTable/dataTableImportButton'
import { ImportColumn } from 'components/common/importModal'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'
import SearchBar from 'components/common/dataTable/searchBar'
import { useQuery } from 'hooks'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Pagination } from 'types/user'
import {
  fetchPosAreaCoordinates,
  getPosAreasList,
  importPosAreas,
} from 'utils/api/ares'

interface PosAreaListDataType {
  _id: string
  posArea: string | null
  loginextArea: string | null
  deliveryTime: number | null
  areaLat: number | null
  areaLong: number | null
  updatedAt: Date
}

export type PosAreaResponse = Pagination & {
  docs: PosAreaListDataType[] | []
}

const PosAreaList = ({ isEditor }: { isEditor: boolean }) => {
  const [loading, setLoading] = useState(false)
  const [coordLoading, setCoordLoading] = useState(false)
  const [posAreaImportColumns, setPosAreaImportColumns] = useState<
    ImportColumn[]
  >([])
  const [posAreas, setPosAreas] = useState<PosAreaResponse | null>(null)

  const { getAllQueries } = useQuery()

  const { search } = useLocation()

  const getPosArea = async () => {
    setLoading(true)
    const { limit, search: SearchTerm, page } = getAllQueries()

    try {
      const { data } = await getPosAreasList({
        limit: limit || 10,
        page: page || 1,
        search: SearchTerm,
      })

      setPosAreas(data)
    } catch (error) {
      setPosAreas(null)
    } finally {
      setLoading(false)
    }
  }

  const fetchCoords = async () => {
    const loadingToast = toast.loading('Fetching coordinates...', {
      duration: Infinity,
    })
    setCoordLoading(true)

    try {
      await fetchPosAreaCoordinates()
      await getPosArea()
      toast.success('Coordinates fetched successfully', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      toast.error('Failed to fetch coordinates', {
        id: loadingToast,
        duration: 3000,
      })
    } finally {
      setCoordLoading(false)
    }
  }
  useEffect(() => {
    getPosArea()
  }, [search])

  useEffect(() => {
    getImportTemplate('posAreas')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setPosAreaImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadPosAreasSample = async () => {
    const response = await downloadImportSample('posAreas')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'pos_areas_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const renderAreasRows = useMemo(() => {
    if (!posAreas?.docs || posAreas.docs.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="py-8 text-center text-gray-500">
            {loading ? 'Loading...' : 'No areas found'}
          </td>
        </tr>
      )
    }

    return posAreas.docs.map((area) => (
      <tr key={area._id} className="border-b border-gray-200 hover:bg-gray-50">
        <td className="px-4 py-3 font-medium">{area.posArea}</td>
        <td className="px-4 py-3 font-medium">{area.loginextArea}</td>
        <td className="px-4 py-3 font-medium">{area.deliveryTime}</td>
        <td className="px-4 py-3 font-medium">{area.areaLat}</td>
        <td className="px-4 py-3 font-medium">{area.areaLong}</td>
      </tr>
    ))
  }, [posAreas?.docs, loading])

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex flex-col">
          <h3 className="mb-6 text-2xl font-bold">POS Area Mapping</h3>
          <div className="flex w-full items-center justify-between">
            <SearchBar placeholder={'Search by Area name'} disabled={loading} />
            <div className="flex items-center gap-2">
              {isEditor && (
                <Button
                  type="button"
                  variant="outline"
                  buttonText="Fetch Coordinates"
                  loading={coordLoading}
                  onClick={fetchCoords}
                />
              )}
              {isEditor && importPosAreas && (
                <DataTableImportButton
                  disabled={loading}
                  uploadApiFunction={importPosAreas}
                  importColumns={posAreaImportColumns}
                  importModuleName="POS area mapping"
                  onDownloadSample={handleDownloadPosAreasSample}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="h-full max-h-[600px] overflow-y-scroll">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
                <th className="px-4 py-3">POS Area</th>
                <th className="px-4 py-3">Loginext Area</th>
                <th className="px-4 py-3">Delivery Time</th>
                <th className="px-4 py-3">Area Lat</th>
                <th className="px-4 py-3">Area Long</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {renderAreasRows}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-6">
        <DataTablePagination
          currentPage={posAreas?.page}
          totalPages={posAreas?.totalPages}
          hasNextPage={posAreas?.hasNextPage}
          hasPrevPage={posAreas?.hasPrevPage}
          limit={posAreas?.limit}
          nextPage={posAreas?.nextPage}
          prevPage={posAreas?.prevPage}
        />
      </div>
    </div>
  )
}

export default PosAreaList
